import { useEffect, useMemo, useState } from "react";
import { useLibraryStore } from "../store/libraryStore";
import { useTripStore } from "../store/tripStore";
import { useUIStore } from "../store/uiStore";
import { epochMs } from "../utils/zonedTime";
import { formatTimeBudget } from "../utils/format";
import { poiMapsUrl } from "../utils/poiMaps";
import { Modal } from "./Modal";
import { Button } from "./Button";
import {
  TimezoneChangeNotice,
  eventTimeFieldsError,
  eventTimeFieldsFromZoned,
  zonedFromEventTimeFields,
  type EventTimeFieldsValue,
} from "./EventTimingFields";
import {
  EventEditorAlert,
  EventEditorSection,
  EventTimingSection,
} from "./EventEditorSection";
import { resolveTagNames } from "../utils/tagDisplay";

/** Edit one scheduled visit without mutating its reusable library POI. */
export function ScheduledVisitEditorModal() {
  const activeEditor = useUIStore((s) => s.activeEditor);
  const timingProposal = useUIStore((s) => s.timingProposal);
  const closeEditor = useUIStore((s) => s.closeEditor);
  const openPOIEditor = useUIStore((s) => s.openPOIEditor);
  const schedule = useTripStore((s) => s.schedule);
  const trip = useTripStore((s) => s.trip);
  const updateScheduledPoi = useTripStore((s) => s.updateScheduledPoi);
  const isReadOnly = useTripStore((s) => s.isReadOnly);
  const pois = useLibraryStore((s) => s.pois);
  const tags = useLibraryStore((s) => s.tags);

  const isOpen = activeEditor?.type === "scheduledVisit";
  const scheduledVisit = isOpen
    ? schedule.scheduledPOIs.find((item) => item.id === activeEditor.entityId)
    : undefined;
  const poi = scheduledVisit
    ? pois.find((item) => item.id === scheduledVisit.poiId)
    : undefined;
  const tagNames = poi ? resolveTagNames(poi.tags, tags) : [];

  const [start, setStart] = useState<EventTimeFieldsValue | null>(null);
  const [end, setEnd] = useState<EventTimeFieldsValue | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!scheduledVisit) return;
    const proposal =
      timingProposal?.eventType === "scheduledVisit" &&
      timingProposal.entityId === scheduledVisit.id
        ? timingProposal
        : null;
    setStart(
      eventTimeFieldsFromZoned(proposal?.startTime ?? scheduledVisit.startTime),
    );
    setEnd(
      eventTimeFieldsFromZoned(proposal?.endTime ?? scheduledVisit.endTime),
    );
    setError(null);
  }, [scheduledVisit, timingProposal]);

  const activeTimingProposal =
    scheduledVisit &&
    timingProposal?.eventType === "scheduledVisit" &&
    timingProposal.entityId === scheduledVisit.id
      ? timingProposal
      : null;

  const mapsUrl = useMemo(() => (poi ? poiMapsUrl(poi) : null), [poi]);
  const readOnly = isReadOnly();
  const editable = !!scheduledVisit && !readOnly && !scheduledVisit.locked;

  const handleSave = () => {
    if (!scheduledVisit || !start || !end || !trip) return;
    if (!start.date || !start.time || !end.date || !end.time) {
      setError("Enter both a start and end date and time.");
      return;
    }
    if (start.date < trip.startDate || end.date > trip.endDate) {
      setError("Visit dates must remain within the trip date range.");
      return;
    }

    const startFieldError = eventTimeFieldsError(start);
    const endFieldError = eventTimeFieldsError(end);
    if (startFieldError || endFieldError) {
      setError(startFieldError ?? endFieldError);
      return;
    }
    const startTime = zonedFromEventTimeFields(start);
    const endTime = zonedFromEventTimeFields(end);
    if (!startTime || !endTime) {
      setError("Choose valid start and end wall times.");
      return;
    }
    if (epochMs(endTime) <= epochMs(startTime)) {
      setError("End must be after start.");
      return;
    }

    updateScheduledPoi(scheduledVisit.id, { startTime, endTime });
    closeEditor();
  };

  const openPoiDetails = () => {
    if (poi) openPOIEditor("edit", poi.id);
  };

  return (
    <Modal
      open={isOpen}
      onClose={closeEditor}
      title={poi?.name ?? "Scheduled visit"}
      description="Edit this visit without changing the linked library POI."
      icon="event"
      size="xl"
      footer={
        <>
          <Button onClick={closeEditor} variant="secondary">
            {editable ? "Cancel" : "Close"}
          </Button>
          {editable && (
            <Button onClick={handleSave} variant="primary">
              Save visit
            </Button>
          )}
        </>
      }
    >
      {!scheduledVisit || !poi || !start || !end ? (
        <p className="text-sm text-secondary">
          This scheduled visit is no longer available.
        </p>
      ) : (
        <div className="flex flex-col gap-5">
          {activeTimingProposal && (
            <TimezoneChangeNotice proposal={activeTimingProposal} />
          )}
          <EventEditorSection
            id="visit-details-heading"
            title="Visit details"
            description="The linked library place remains reusable across trips."
            icon="location_on"
          >
            <div className="rounded-xl border border-border bg-surface p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <span className="material-symbols-outlined rounded-lg bg-sky-50 p-2 text-xl text-info">
                  location_on
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-primary">{poi.name}</div>
                  <div className="mt-0.5 text-xs font-semibold text-secondary">
                    Default visit {formatTimeBudget(poi.timeBudget)}
                    {tagNames.length ? ` · ${tagNames.join(", ")}` : ""}
                  </div>
                  {poi.notes && (
                    <p className="mb-0 mt-2 line-clamp-3 text-sm text-secondary">
                      {poi.notes}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-3">
                {mapsUrl && (
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-bold text-primary no-underline transition-colors hover:bg-subtle"
                  >
                    <span className="material-symbols-outlined text-base">
                      map
                    </span>
                    View in Maps
                  </a>
                )}
                <Button
                  onClick={openPoiDetails}
                  variant="ghost"
                  size="sm"
                  icon="edit"
                >
                  Edit POI details
                </Button>
              </div>
            </div>
          </EventEditorSection>

          <EventTimingSection
            id="visit-timing-heading"
            start={start}
            end={end}
            onStartChange={setStart}
            onEndChange={setEnd}
            disabled={!editable}
          >
            {scheduledVisit.locked && !readOnly && (
              <p className="mb-0 mt-3 flex items-center gap-1.5 px-1 text-xs font-semibold text-secondary">
                <span className="material-symbols-outlined text-sm" aria-hidden>
                  lock
                </span>
                Unlock this visit on the calendar before changing its time.
              </p>
            )}
          </EventTimingSection>

          <EventEditorSection
            id="visit-notes-heading"
            title="Visit notes"
            description="Instance-specific context stays with this scheduled visit."
            icon="notes"
          >
            <div
              data-testid="scheduled-visit-notes-host"
              className="rounded-xl border border-dashed border-border px-4 py-4"
            >
              <p className="m-0 text-sm font-semibold text-secondary">
                Visit-specific notes will appear here.
              </p>
            </div>
          </EventEditorSection>

          {error && <EventEditorAlert>{error}</EventEditorAlert>}
        </div>
      )}
    </Modal>
  );
}
